/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateXAction,
  useStateXAtom,
  useStateXAtomSetter,
  useStateXAtomValue,
  useStateXSetter,
  useStateXValue,
} from '@cloudio-saas/statex';
import { Divider, Theme, Menu, MenuItem, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  activeSidebarPanelAtom,
  runtimeContextMenuAtom,
  sidebarDataSourceFilterAtom,
  sidebarDSViewAtom,
  sidebarPagesFilterAtom,
} from 'layout/Sidebar/atoms';
import React, { useRef } from 'react';
import useUser, { isImpersonatedDeveloper, useIsDeveloper } from 'root/useUser';
import { activeItemSelector } from '../designer/activeItemSelector';
import Icon from '../icons/Icon';
import { useUpdateOnHMR } from '../utils/hmr';
import {
  developerModeAtom,
  hideDevToolbarAtom,
  hideHeaderAtom,
  hideSidebarAtom,
} from '../designer/atoms';
import { usePlatform } from './PlatformHooks';
import { TreeTypeDesigner } from '../layout/Sidebar/panels/TreeView/state';
import {
  examineDataAliasAtom,
  examineDataPageIdAtom,
  openAboutAtom,
  openTestRecordingDialogAtom,
  openViewDatasourceListAtom,
  recentSqlAliasFilterAtom,
  showExaminePropsAtom,
  viewDatasourcesUsedInfoAtom,
} from '../layout/Header/HeaderState';
import { activeTabAtom, debugConsoleMinimizedAtom } from './console/state';
import { enableDebugConsoleAtom } from 'layout/Sidebar/panels/SettingsView/settings/OptionSettings';
import { useOrgNameAndLogo } from 'layout/Header/OrgLogo';
import { getManager } from '../componentregistry';
import { captureScreenAtom } from '../howto/screenshot';
import { isChrome } from 'utils/browser';
import {
  recordMessagesAtom,
  recordUIMessagesAtom,
} from 'layout/Sidebar/panels/TestsView/state';
import { sidebarToggleAction } from 'layout/Sidebar/panels/ExpandCollapseIcon';
import TestAssertionMenu from './TestAssertionMenu';

const useStyles = makeStyles((theme: Theme) => ({
  menu: {
    '& $searchFocus': {
      backgroundColor: 'transparent',
    },
  },
  itemText: {
    maxWidth: 360,
    whiteSpace: 'normal',
  },
  menuItem: {
    minWidth: 180,
  },
  icon: {
    marginRight: 12,
  },
  search: {
    border: 0,
    width: '100%',
  },
  searchFocus: {},
  embed: {
    fontSize: '0.8em',
    color: theme.palette.text.secondary,
  },
}));

export function GoToDataSourceMenu({
  appUid,
  pageId,
  itemId,
  dsAlias,
  onClose,
}: {
  appUid: string;
  pageId: string;
  itemId: string;
  dsAlias: string;
  onClose: () => void;
}): React.ReactElement | null {
  const platform = usePlatform();
  const classes = useStyles();
  const set = useStateXSetter();
  return dsAlias ? (
    <MenuItem
      key="alias"
      className={classes.menuItem}
      onClick={() => {
        onClose();
        set(activeSidebarPanelAtom, 'ds');
        const store = platform.getStore(appUid, pageId, dsAlias);
        if (store) {
          set(sidebarDataSourceFilterAtom, `ds:${store.ds}`);
          set(sidebarDSViewAtom, 'list');
        }
      }}
    >
      <Icon className={classes.icon} iconName="database" />
      Go To Data Source {dsAlias}
    </MenuItem>
  ) : null;
}

export default function RuntimeDeveloperContextMenu({
  appUid,
  pageId: apageId,
}: {
  appUid: string;
  pageId: string;
}): React.ReactElement | null {
  const [hideHeader, setHideHeader] = useStateXAtom(hideHeaderAtom);
  const [hideSidebar, setHideSidebar] = useStateXAtom(hideSidebarAtom);
  const [hideDevToolbar, setHideDevToolbar] = useStateXAtom(hideDevToolbarAtom);
  const recording = useStateXAtomValue(recordMessagesAtom);
  const uirecording = useStateXAtomValue(recordUIMessagesAtom);
  const closeSidebar = useStateXAction(sidebarToggleAction);
  const setShowAbout = useStateXAtomSetter(openAboutAtom);
  const setOpenTestRecordingDialog = useStateXAtomSetter(
    openTestRecordingDialogAtom,
  );
  const openDSList = useStateXAtomSetter(openViewDatasourceListAtom);
  const [{ orgName }] = useOrgNameAndLogo();
  const platform = usePlatform();
  const [{ pageId, itemId, orphan, dsAlias, rowIndex, ...point }, setPoint] =
    useStateXAtom(runtimeContextMenuAtom);
  const setViewDatasourcesUsedInfo = useStateXAtomSetter(
    viewDatasourcesUsedInfoAtom,
  );
  const [captureScreen, setCaptureScreen] = useStateXAtom(captureScreenAtom);
  const item = itemId ? platform.getItem(appUid, pageId, itemId) : undefined;
  const classes = useStyles();
  const set = useStateXSetter();
  const ref = useRef<HTMLUListElement>(null);
  const { userName } = useUser();
  useUpdateOnHMR();
  const developer = useIsDeveloper(appUid);
  const name = item ? getManager(item.type).name : undefined;
  const pageName = useStateXValue(['db', 'IoPages', pageId, 'pageName'], '');

  if (point.left === 0 || isImpersonatedDeveloper(platform)) {
    return null;
  }

  function close() {
    setPoint((p) => ({ ...p, left: 0, top: 0 }));
  }

  let embeddedPageId = '';
  if (pageId !== platform.pageId) {
    embeddedPageId = pageId;
  }

  if (platform.isRecording()) {
    return (
      <Menu
        open
        className={classes.menu}
        onClose={close}
        anchorReference="anchorPosition"
        anchorPosition={point}
        MenuListProps={{ ref }}
        BackdropProps={{ 'aria-label': 'Menu Backdrop' }}
      >
        <MenuItem
          key="recording"
          className={classes.menuItem}
          onClick={() => {
            close();
          }}
        >
          <Icon className={classes.icon} iconName="circle-video" />
          {'Recording in Progress...'}
        </MenuItem>
        <TestAssertionMenu
          itemId={itemId}
          item={item}
          platform={platform}
          rowIndex={rowIndex}
          onClose={close}
        />
      </Menu>
    );
  }

  return (
    <Menu
      open
      className={classes.menu}
      onClose={close}
      anchorReference="anchorPosition"
      anchorPosition={point}
      MenuListProps={{ ref }}
      BackdropProps={{ 'aria-label': 'Menu Backdrop' }}
    >
      <MenuItem
        key="data"
        className={classes.menuItem}
        onClick={() => {
          close();
          set(activeSidebarPanelAtom, 'pages');
          set(sidebarPagesFilterAtom, `pg:${pageName}`);
        }}
      >
        <Icon className={classes.icon} iconName="browser" />
        {`Go To Page ${pageName} (${pageId})`}
      </MenuItem>
      {item ? (
        <MenuItem
          key="go_props"
          className={classes.menuItem}
          onClick={() => {
            close();
            set(developerModeAtom, true, {
              params: { appUid },
            });
            if (embeddedPageId) {
              platform.goToPage(appUid, embeddedPageId);
            }
            set(activeSidebarPanelAtom, 'props');
            setTimeout(() => {
              set(
                activeItemSelector,
                {
                  appUid,
                  pageId,
                  itemId,
                  expand: false,
                  treename: TreeTypeDesigner,
                },
                {
                  params: { appUid, treename: TreeTypeDesigner, pageId },
                  props: platform,
                },
              );
            }, 100);
          }}
        >
          <Icon className={classes.icon} iconName="list-alt" />
          Go To {name} Properties
        </MenuItem>
      ) : null}
      {dsAlias ? (
        <GoToDataSourceMenu
          appUid={appUid}
          dsAlias={dsAlias}
          itemId={itemId}
          key="alias"
          onClose={close}
          pageId={pageId}
        />
      ) : null}
      <Divider key="spdivider" />
      <MenuItem
        key="data"
        className={classes.menuItem}
        onClick={() => {
          close();
          set(examineDataAliasAtom, dsAlias);
          set(examineDataPageIdAtom, pageId);
          set(activeTabAtom, 1);
          set(debugConsoleMinimizedAtom, false, { params: { userName } });
          set(
            enableDebugConsoleAtom,
            (val) => (val === 'Hide' ? 'Show' : val),
            { params: { userName } },
          );
        }}
      >
        <Icon className={classes.icon} iconName="brackets-curly" />
        Inspect Data
      </MenuItem>
      <MenuItem
        key="sql"
        className={classes.menuItem}
        onClick={() => {
          close();
          set(recentSqlAliasFilterAtom, dsAlias);
          set(activeTabAtom, 2);
          set(debugConsoleMinimizedAtom, false, { params: { userName } });
          set(
            enableDebugConsoleAtom,
            (val) => (val === 'Hide' ? 'Show' : val),
            { params: { userName } },
          );
        }}
      >
        <Icon className={classes.icon} iconName="database" />
        View Recent SQL
      </MenuItem>
      {item ? (
        <MenuItem
          key="props"
          className={classes.menuItem}
          onClick={() => {
            close();
            set(showExaminePropsAtom, { appUid, pageId, itemId });
          }}
        >
          <Icon className={classes.icon} iconName="code" />
          Examine {name} Properties
        </MenuItem>
      ) : null}
      <MenuItem
        data-testid="ds-list"
        onClick={() => {
          close();
          setViewDatasourcesUsedInfo({
            pageId,
          });
          openDSList(true);
        }}
      >
        <Icon className={classes.icon} iconName="coins" />
        <Typography>View Referred Datasources</Typography>
      </MenuItem>
      {developer ? <Divider key="d2" /> : null}
      {developer ? (
        <MenuItem
          key="hideheader"
          className={classes.menuItem}
          onClick={() => {
            close();
            setHideHeader(!hideHeader);
          }}
        >
          <Icon
            className={classes.icon}
            iconName={hideHeader ? 'eye' : 'eye-slash'}
          />
          {hideHeader ? 'Show' : 'Hide'} Header
        </MenuItem>
      ) : null}
      {developer ? (
        <MenuItem
          key="hidesidebar"
          className={classes.menuItem}
          onClick={() => {
            close();
            setHideSidebar(!hideSidebar);
          }}
        >
          <Icon
            className={classes.icon}
            iconName={hideSidebar ? 'eye' : 'eye-slash'}
          />
          {hideSidebar ? 'Show' : 'Hide'} Sidebar
        </MenuItem>
      ) : null}
      {developer ? (
        <MenuItem
          key="hidedt"
          className={classes.menuItem}
          onClick={() => {
            close();
            setHideDevToolbar(!hideDevToolbar);
          }}
        >
          <Icon
            className={classes.icon}
            iconName={hideDevToolbar ? 'eye' : 'eye-slash'}
          />
          {hideDevToolbar ? 'Show' : 'Hide'} Developer Toolbar
        </MenuItem>
      ) : null}
      {developer ? (
        <MenuItem
          key="cs"
          className={classes.menuItem}
          onClick={() => {
            close();
            setCaptureScreen({ active: !captureScreen.active, appUid });
          }}
          data-nocapture
          disabled={!isChrome()}
        >
          <Icon
            className={classes.icon}
            iconName={captureScreen.active ? 'stop' : 'play'}
          />
          {captureScreen.active ? 'Stop' : 'Start'} Screen Capture
          {!isChrome() ? ' (Chrome Only)' : ''}
        </MenuItem>
      ) : null}
      {developer && !(recording || uirecording) ? (
        <MenuItem
          key="test-script"
          className={classes.menuItem}
          onClick={() => {
            close();
            closeSidebar({ open: false });
            setOpenTestRecordingDialog(true);
          }}
          data-nocapture
        >
          <Icon className={classes.icon} iconName="video-plus" />
          {'Record New Test Script'}
        </MenuItem>
      ) : null}
      {developer ? <Divider key="adivider" /> : null}
      <MenuItem
        data-testid="about"
        onClick={() => {
          close();
          setShowAbout(true);
        }}
      >
        <Icon className={classes.icon} iconName="desktop" />
        <Typography>About {orgName}</Typography>
      </MenuItem>
    </Menu>
  );
}
